import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { books, bookOfMonth, buyLink } from "../mock";
import { ArrowRight, ExternalLink } from "lucide-react";

const BookCover = ({ book, className = "" }) => {
  if (book.cover) {
    return (
      <div
        className={`relative w-full aspect-[3/4] rounded-md overflow-hidden shadow-md bg-white flex items-center justify-center p-2 ${className}`}
      >
        <img
          src={book.cover}
          alt={`${book.title} by ${book.author}`}
          className="max-w-full max-h-full w-auto h-auto object-contain"
          loading="lazy"
        />
      </div>
    );
  }
  return (
    <div
      className={`relative w-full aspect-[3/4] rounded-md overflow-hidden shadow-md flex flex-col justify-between p-5 ${className}`}
      style={{ backgroundColor: book.color }}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-black/20" />
      <span className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-white/70">
        20 Proof Reads
      </span>
      <div>
        <h3 className="font-display text-white text-2xl leading-tight" style={{ fontWeight: 600 }}>
          {book.title}
        </h3>
        <p className="font-display italic text-white/80 text-sm mt-2">{book.author}</p>
      </div>
    </div>
  );
};

export { BookCover };

const ResourcesPage = () => {
  return (
    <div className="bg-[#eceae5] min-h-screen">
      <SEO title="Books" description="Our top book recommendations for your twenties — the reads we keep coming back to." type="website" />
      <Navbar solid />

      <header className="pt-36 md:pt-44 pb-10 px-6 md:px-10 max-w-[1400px] mx-auto">
        <p className="font-sans-ui uppercase tracking-[0.2em] text-sm text-neutral-500">Resources</p>
        <h1 className="font-display text-5xl md:text-7xl text-neutral-900 mt-3" style={{ fontWeight: 700 }}>
          Books
        </h1>
        <p className="font-display italic text-neutral-600 text-lg md:text-xl mt-4 max-w-2xl">
          Some of our top recommendations for your next reads — the ones we keep coming back to in our twenties.
        </p>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        {/* Book of the Month */}
        <div className="mb-16 rounded-xl overflow-hidden bg-white/60 border border-neutral-200">
          <div className="grid md:grid-cols-[280px_1fr] gap-8 md:gap-12 p-6 md:p-10 items-center">
            <Link to={`/resources/${bookOfMonth.slug}`} className="max-w-[240px] md:max-w-[280px] mx-auto w-full transition-transform duration-500 hover:-translate-y-1.5">
              <BookCover book={bookOfMonth} />
            </Link>
            <div>
              <p className="font-sans-ui uppercase tracking-[0.2em] text-xs text-neutral-500">Book of the Month</p>
              <Link to={`/resources/${bookOfMonth.slug}`}>
                <h2 className="font-display text-3xl md:text-4xl text-neutral-900 mt-3 leading-tight hover:opacity-70 transition-opacity" style={{ fontWeight: 700 }}>
                  {bookOfMonth.title}
                </h2>
              </Link>
              <p className="font-display italic text-neutral-600 mt-1">by {bookOfMonth.author}</p>
              <p className="font-display text-neutral-800 mt-4 leading-relaxed line-clamp-4">{bookOfMonth.why[0]}</p>
              <div className="flex flex-wrap items-center gap-3 mt-6">
                <a
                  href={buyLink(bookOfMonth)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-neutral-300 hover:border-neutral-900 text-neutral-800 font-sans-ui text-sm px-7 py-3.5 rounded-full transition-colors"
                >
                  <ExternalLink size={15} /> Find this book
                </a>
                <Link
                  to={`/resources/${bookOfMonth.slug}`}
                  className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-700 text-white font-sans-ui text-sm px-7 py-3.5 rounded-full transition-colors"
                >
                  Read more <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-12">
          {books.map((book) => (
            <div key={book.slug} className="group flex flex-col">
              <Link to={`/resources/${book.slug}`}>
                <div className="transition-transform duration-500 group-hover:-translate-y-1.5">
                  <BookCover book={book} />
                </div>
              </Link>
              <p className="font-sans-ui text-xs uppercase tracking-widest text-neutral-500 mt-4">{book.tag}</p>
              <Link to={`/resources/${book.slug}`}>
                <h3 className="font-display text-xl text-neutral-900 mt-1 leading-snug group-hover:opacity-70 transition-opacity" style={{ fontWeight: 700 }}>
                  {book.title}
                </h3>
              </Link>
              <p className="font-display italic text-neutral-600 mt-1 text-sm">{book.author}</p>
              <p className="font-display text-neutral-600 mt-2 leading-relaxed text-sm">{book.blurb}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                <a
                  href={buyLink(book)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 font-sans-ui text-sm text-neutral-500 hover:text-neutral-900 transition-colors"
                >
                  <ExternalLink size={14} /> Find this book
                </a>
                <Link to={`/resources/${book.slug}`} className="inline-flex items-center gap-1.5 font-sans-ui text-sm text-neutral-900">
                  Read more <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResourcesPage;
